import axios from 'axios';

import { withAuthRedirect } from '../service/api';
import { CLIENT_SOURCE, CLIENT_SOURCE_HEADER } from './constant';
import { getUserAccessToken } from './setting';

const TIMEOUT_MS = 45000;
const mediaClient = withAuthRedirect(axios.create({ timeout: TIMEOUT_MS }));

const STATUS_MESSAGE: Record<number, string> = {
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have access to this file.',
  404: 'This file is no longer available.',
  410: 'This file is no longer available.',
  429: 'Too many requests. Please wait a moment and try again.',
};

const messageFromErrorBlob = async (data: unknown): Promise<string> => {
  try {
    const text =
      typeof (data as Blob)?.text === 'function'
        ? await (data as Blob).text()
        : typeof data === 'string'
          ? data
          : '';

    return String(JSON.parse(text)?.message || '');
  } catch {
    return '';
  }
};

const getBlob = async (url: string, emptyMessage: string): Promise<Blob> => {
  const token = getUserAccessToken();

  const response = await mediaClient
    .get(url, {
      responseType: 'blob',
      headers: {
        ...(token ? { Authorization: token } : {}),
        [CLIENT_SOURCE_HEADER]: CLIENT_SOURCE,
      },
    })
    .catch(async (error: any) => {
      const status = Number(error?.response?.status) || 0;

      if (!status) {
        throw new Error('Could not reach storage. Check your connection and try again.');
      }

      const served = await messageFromErrorBlob(error?.response?.data);

      throw new Error(
        served ||
          STATUS_MESSAGE[status] ||
          (status >= 500
            ? 'Storage is having trouble right now. Please try again.'
            : 'This file could not be loaded.'),
      );
    });

  const blob = response?.data as Blob | undefined;

  if (!blob || (typeof blob.size === 'number' && blob.size === 0)) {
    throw new Error(emptyMessage);
  }

  return blob;
};

const revoke = (objectUrl?: string | null) => {
  if (!objectUrl) return;
  try {
    URL.revokeObjectURL(objectUrl);
  } catch {
    /* already revoked */
  }
};

/** Any protected asset — images, csv, anything. */
export const fetchAuthedBlobUrl = async (
  url: string,
): Promise<{ blob: Blob; objectUrl: string }> => {
  if (!url) {
    throw new Error('No URL supplied');
  }

  const blob = await getBlob(url, 'Empty response');

  return { blob, objectUrl: URL.createObjectURL(blob) };
};

export const revokeAuthedBlobUrl = revoke;

export const fetchMediaAudioUrl = async (url: string): Promise<string> => {
  if (!url) {
    throw new Error('No media URL supplied');
  }

  const blob = await getBlob(url, 'Empty audio response');

  if (blob.type && !blob.type.startsWith('audio/')) {
    throw new Error(
      blob.type.includes('application/json')
        ? 'Audio not available'
        : `Unexpected response type "${blob.type}" — audio endpoint may be misrouted`,
    );
  }

  return URL.createObjectURL(blob);
};

export const revokeMediaAudioUrl = revoke;
